/**
 * Content Block Web Component
 * 
 * Handles positioning logic to hide duplicate content blocks based on their
 * row and column positions for both desktop and mobile breakpoints.
 * Now accounts for row and column spanning.
 */
class ContentBlock extends HTMLElement {
  constructor() {
    super();
    this.#init();
  }

  connectedCallback() {
    // Wait for DOM to be ready and other content blocks to be rendered
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#handlePositioning());
    } else {
      // Use requestAnimationFrame to ensure all content blocks are rendered
      requestAnimationFrame(() => this.#handlePositioning());
    }
  }

  /**
   * Initialize the component
   */
  #init() {
    this.desktopPosition = this.#getDesktopPosition();
    this.mobilePosition = this.#getMobilePosition();
  }

  /**
   * Get desktop position data with span information
   * @returns {Object} Desktop position with row, column, and occupied positions
   */
  #getDesktopPosition() {
    const row = parseInt(this.dataset.contentBlockRowPosition || '1', 10);
    const column = parseInt(this.dataset.contentBlockColumnPosition || '1', 10);
    const rowSpan = parseInt(this.dataset.contentBlockRowSpan || '1', 10);
    const columnSpan = parseInt(this.dataset.contentBlockColumnSpan || '1', 10);
    
    // Calculate all grid positions this content block occupies
    const occupiedPositions = [];
    for (let r = row; r < row + rowSpan; r++) {
      for (let c = column; c < column + columnSpan; c++) {
        occupiedPositions.push(`${r}-${c}`);
      }
    }
    
    return {
      row,
      column,
      rowSpan,
      columnSpan,
      occupiedPositions,
      key: `${row}-${column}`, // Primary position key
      occupiedKeys: occupiedPositions // All positions occupied
    };
  }

  /**
   * Get mobile position data with span information
   * @returns {Object} Mobile position with row, column, and occupied positions
   */
  #getMobilePosition() {
    const row = parseInt(this.dataset.contentBlockRowPositionMobile || '1', 10);
    const column = parseInt(this.dataset.contentBlockColumnPositionMobile || '1', 10);
    const rowSpan = parseInt(this.dataset.contentBlockRowSpanMobile || '1', 10);
    const columnSpan = parseInt(this.dataset.contentBlockColumnSpanMobile || '2', 10);
    
    // Calculate all grid positions this content block occupies on mobile
    const occupiedPositions = [];
    for (let r = row; r < row + rowSpan; r++) {
      for (let c = column; c < column + columnSpan; c++) {
        occupiedPositions.push(`${r}-${c}`);
      }
    }
    
    return {
      row,
      column,
      rowSpan,
      columnSpan,
      occupiedPositions,
      key: `${row}-${column}`, // Primary position key
      occupiedKeys: occupiedPositions // All positions occupied
    };
  }

  /**
   * Handle positioning logic and hide duplicates
   */
  #handlePositioning() {
    const allContentBlocks = document.querySelectorAll('content-block[data-content-block]');
    
    if (allContentBlocks.length <= 1) return;

    // Check for overlapping content blocks
    this.#checkForOverlaps(allContentBlocks, 'desktop');
    this.#checkForOverlaps(allContentBlocks, 'mobile');
  }

  /**
   * Check for overlapping content blocks and hide duplicates
   * @param {NodeList} allContentBlocks - All content block elements
   * @param {string} breakpoint - 'desktop' or 'mobile'
   */
  #checkForOverlaps(allContentBlocks, breakpoint) {
    const processedBlocks = new Set();
    
    allContentBlocks.forEach((block, index) => {
      if (processedBlocks.has(block)) return;
      
      const currentPosition = breakpoint === 'desktop' ? block.desktopPosition : block.mobilePosition;
      const overlappingBlocks = [block];
      
      // Find all blocks that overlap with the current block
      allContentBlocks.forEach((otherBlock, otherIndex) => {
        if (otherIndex <= index || processedBlocks.has(otherBlock)) return;
        
        const otherPosition = breakpoint === 'desktop' ? otherBlock.desktopPosition : otherBlock.mobilePosition;
        
        // Check if any of the occupied positions overlap
        const hasOverlap = currentPosition.occupiedKeys.some(pos => 
          otherPosition.occupiedKeys.includes(pos)
        );
        
        if (hasOverlap) {
          overlappingBlocks.push(otherBlock);
        }
      });
      
      // If we found overlapping blocks, hide all but the first
      if (overlappingBlocks.length > 1) {
        overlappingBlocks.slice(1).forEach(block => {
          this.#hideForBreakpoint(block, breakpoint);
          processedBlocks.add(block);
        });
      }
      
      processedBlocks.add(block);
    });
  }

  /**
   * Hide a content block for a specific breakpoint
   * @param {HTMLElement} block - The content block element
   * @param {string} breakpoint - 'desktop' or 'mobile'
   */
  #hideForBreakpoint(block, breakpoint) {
    if (breakpoint === 'desktop') {
      block.classList.add('content-block--hidden-desktop');
    } else {
      block.classList.add('content-block--hidden-mobile');
    }
  }
}

// Register the custom element
if (!customElements.get('content-block')) {
  customElements.define('content-block', ContentBlock);
}
